import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Download } from 'lucide-react';
import { cn } from '@/lib/utils';

// Player de áudio da conversa. O <audio controls> nativo tem visual de 2010 e
// tamanho fixo que não cabe na bolha — este usa o design system e ocupa a
// largura da mensagem.
//
// A forma de onda é decorativa e determinística (derivada da URL), não uma
// análise real do arquivo: decodificar o áudio para desenhar picos custaria um
// fetch + decodeAudioData por mensagem, e o ganho é só estético.

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Barras estáveis por áudio: mesma URL, mesmo desenho entre re-renders.
function gerarOnda(seed: string, barras = 32) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return Array.from({ length: barras }, (_, i) => {
    h = (h * 1103515245 + 12345) >>> 0;
    const base = 0.25 + ((h >>> 16) % 100) / 133;
    // Leve envelope para não parecer ruído puro.
    const envelope = Math.sin((i / barras) * Math.PI) * 0.35 + 0.65;
    return Math.min(1, base * envelope);
  });
}

// Velocidades no ciclo do botão. Áudio de produtor costuma ser longo e
// arrastado; 1.5x e 2x economizam o tempo do atendente.
const VELOCIDADES = [1, 1.5, 2] as const;

const EVENTO_TOCANDO = 'suporte:audio-tocando';

export function AudioPlayer({ url, outgoing }: { url: string; outgoing: boolean }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [tocando, setTocando] = useState(false);
  const [atual, setAtual] = useState(0);
  const [duracao, setDuracao] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [velocidade, setVelocidade] = useState<number>(1);

  const onda = gerarOnda(url);
  const progresso = duracao > 0 ? atual / duracao : 0;

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onTime = () => setAtual(el.currentTime);
    const onMeta = () => setDuracao(el.duration);
    const onEnd = () => {
      setTocando(false);
      setAtual(0);
    };
    const onWaiting = () => setCarregando(true);
    const onPlaying = () => setCarregando(false);

    // Um áudio por vez: quando outro player começa, este pausa.
    const onOutro = (e: Event) => {
      if ((e as CustomEvent<HTMLAudioElement>).detail !== el && !el.paused) {
        el.pause();
        setTocando(false);
      }
    };
    window.addEventListener(EVENTO_TOCANDO, onOutro);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    el.addEventListener('ended', onEnd);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('playing', onPlaying);
    return () => {
      window.removeEventListener(EVENTO_TOCANDO, onOutro);
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('durationchange', onMeta);
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('playing', onPlaying);
    };
  }, []);

  const alternar = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (tocando) {
      el.pause();
      setTocando(false);
      return;
    }
    try {
      setCarregando(true);
      window.dispatchEvent(new CustomEvent(EVENTO_TOCANDO, { detail: el }));
      await el.play();
      setTocando(true);
    } catch {
      setCarregando(false);
    }
  };

  const ciclarVelocidade = () => {
    const proxima = VELOCIDADES[(VELOCIDADES.indexOf(velocidade as 1) + 1) % VELOCIDADES.length];
    setVelocidade(proxima);
    if (audioRef.current) audioRef.current.playbackRate = proxima;
  };

  // Clique na onda pula para aquele ponto.
  const buscar = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !duracao) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    el.currentTime = Math.max(0, Math.min(duracao, pct * duracao));
    setAtual(el.currentTime);
  };

  return (
    <div
      className={cn(
        'mt-1 flex items-center gap-2.5 rounded-xl px-2.5 py-2',
        outgoing ? 'bg-white/10' : 'bg-card'
      )}
    >
      <audio ref={audioRef} src={url} preload="metadata" />

      <button
        type="button"
        onClick={alternar}
        aria-label={tocando ? 'Pausar áudio' : 'Reproduzir áudio'}
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-full transition-transform active:scale-95',
          outgoing ? 'bg-white text-primary' : 'bg-primary text-white'
        )}
      >
        {carregando && !tocando ? (
          <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : tocando ? (
          <Pause size={15} fill="currentColor" />
        ) : (
          <Play size={15} fill="currentColor" className="ml-0.5" />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div
          onClick={buscar}
          role="slider"
          tabIndex={0}
          aria-label="Posição do áudio"
          aria-valuemin={0}
          aria-valuemax={Math.round(duracao)}
          aria-valuenow={Math.round(atual)}
          className="flex h-7 cursor-pointer items-center gap-[2px]"
        >
          {onda.map((altura, i) => {
            const tocada = i / onda.length <= progresso;
            return (
              <span
                key={i}
                style={{ height: `${Math.round(altura * 100)}%` }}
                className={cn(
                  'w-full rounded-full transition-colors',
                  tocada
                    ? outgoing
                      ? 'bg-white'
                      : 'bg-primary'
                    : outgoing
                      ? 'bg-white/35'
                      : 'bg-border'
                )}
              />
            );
          })}
        </div>
        <div
          className={cn(
            'flex items-center justify-between text-[10px] tabular-nums',
            outgoing ? 'text-white/70' : 'text-text-tertiary'
          )}
        >
          <span>{formatTime(atual)}</span>
          <span>{formatTime(duracao)}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={ciclarVelocidade}
        aria-label={`Velocidade ${velocidade}x`}
        className={cn(
          'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums transition-colors',
          outgoing
            ? 'bg-white/15 text-white/80 hover:bg-white/25'
            : 'bg-bg-main text-text-secondary hover:bg-border'
        )}
      >
        {velocidade}x
      </button>

      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        aria-label="Baixar áudio"
        className={cn(
          'shrink-0 transition-opacity hover:opacity-100',
          outgoing ? 'text-white/60' : 'text-text-tertiary'
        )}
      >
        <Download size={14} />
      </a>
    </div>
  );
}
